// @flow
import https from 'https';
import AppsUtil from './AppsUtil';
import axios from 'axios';
import DeviceDetectionUtil from './DeviceDetectionUtil';
import getConfigApps from '../config';
import thorUtil from './wam/ThorUtil';
import {ffcPath} from '../constants/appsConstants';
import type {FFCProductCallObject} from '../constants/appsConstants';
import {clientTokenExpiryCheck} from './ImsUtils';

class RestApiUtil {
	/**
	 * Returns the x-api-key, Authorization and x-adobe-app-id headers to be sent in a call
	 *
	 * @returns headers
	 */
	getHeaders(withAuth?: boolean, withAppID?: boolean) {
		const headers: Object = {
			'x-api-key': getConfigApps('cchome').clientId
		};
		if (withAuth && window.adobeIMS && window.adobeIMS.getAccessToken() && clientTokenExpiryCheck()) {
			headers.Authorization = `Bearer ${window.adobeIMS.getAccessToken()}`;
		}
		if (withAppID) {
			headers['x-adobe-app-id'] = getConfigApps('cchome').clientId;
		}
		return headers;
	}

	// return pathname for FFC Fallback
	getFFCFallbackEndpoint(type: string, isPlatformRequired: boolean) {
		const ffcPath = `/products/v5/${type}/ccweb${isPlatformRequired ? this.getPlatformFFCQueryParamFallback() : ''}/response.json`;
		return ffcPath;
	}

	// return complete url for FFC Fallback
	getFFCFallbackUrl(type: string, isPlatformRequired: boolean) {
		const servers = getConfigApps('servers');
		const path = `${servers.FFC_fallback}${this.getFFCFallbackEndpoint(type, isPlatformRequired)}`;
		return path;
	}

	// return FFC Fallback promise
	getFFCFallbackProductPromise(type: string, isPlatformRequired: boolean) {
		return axios.get(this.getFFCFallbackUrl(type, isPlatformRequired));
	}

	// returns the product if installed on user machine using wam
	getMachineState(sapCode: ?string) {
		if (sapCode && thorUtil.isConnected() && AppsUtil.appsFFCProductList && AppsUtil.appsFFCProductList[sapCode]) {
			const productFromWAM = thorUtil.getWAMClient().getProduct(sapCode, AppsUtil.appsFFCProductList[sapCode].version);
			if (productFromWAM) {
				const postData = {
					installedProducts: {
						
					}
				};
				postData.installedProducts[sapCode] = [productFromWAM.version];
				return postData;
			}
		}
		return {};
	}

	getFFCUrlPath(channel: string, allVerison: boolean, sapCode: ?string) {
		const {core: coreEndPoint} = ffcPath;
		return `${coreEndPoint}/v5/products${allVerison && sapCode ? `/all?sapCode=${sapCode}&` : '?'}channel=${channel}&platform=${this.getPlatformFFCQueryParam()}`;
	}

	// return ffc call options to fetch data
	getFFCOptions(configObject: FFCProductCallObject, isHttpsReq?: boolean) {
		const servers = getConfigApps('servers');
		const {channel, headers: {withAuth, withAppId}, config: {allVersion, sapCode}, isCCD} = configObject;
		const ffcPath = this.getFFCUrlPath(channel, allVersion, sapCode);

		const options: Object = {
			method: 'post',
			headers: isCCD ? this.getCCDHeaders(withAuth) : this.getHeaders(withAuth, withAppId)
		};

		options.headers['Content-Type'] = 'application/json';
		if (!isHttpsReq) {
			options.data = this.getMachineState(sapCode);
		}

		if (isHttpsReq) {
			options.hostname = `${servers.FFC}`.replace('https://','');
			options.path = ffcPath;
		}
		else {
			options.url = `${servers.FFC}${ffcPath}`;
		}

		return options;
	}

	// ffc fetch data promise
	getFFCProductPromise(configObject: FFCProductCallObject) {
		const options = this.getFFCOptions(configObject);
		const {url, headers, data} = options;

		return axios.post(url, data, {headers: {...headers}});
	}

	getCCDHeaders(withAuth: boolean, AppID?: string) {
		const cchome = getConfigApps('cchome');
		const headers = {};
		headers['x-api-key'] = cchome.clientId;
		headers.Accept = 'application/json';
		if (withAuth && window.adobeIMS && window.adobeIMS.getAccessToken()) {
			headers.Authorization = `Bearer ${window.adobeIMS.getAccessToken()}`;
		}
		if (AppID) {
			headers['x-adobe-app-id'] = AppID;
		}
		else {
			headers['x-adobe-app-id'] = cchome.clientId;
		}
		return headers;
	}

	fetchFFCFallbackProductsJson() {
		const servers = getConfigApps('servers');
		const useNull = (e) => {
			console.log(e);
			return null;
		};
		return axios.get(`${servers.WAM_fallback}/${DeviceDetectionUtil.isWin() ? 'win' : 'mac'}/products.json`).catch(useNull);
	}

	/**
	 * Returns the FFC passphrase needed for download
	 *
	 * @returns FFC passphrase
	 */
	getFFCPassphrase(): Promise<string> {
		return new Promise((resolve, reject) => {
			const request = https.request({
				hostname: getConfigApps('servers').FFC.replace('https://',''),
				path: `/adobe-ffc-external/core/v1/passphrase?passPhrase=${(new Date).getTime()}`,
				withCredentials: false
			}, (res) => {
				let data = '';
				res.on('data', chunk => data += chunk);
				res.on('end', () => resolve(data));
			});

			request.on('error', (e) => {
				reject(e);
			});

			request.end();
		});
	}

	/**
	 * Returns a promise that gives the PDP JSON data corresponding to the CVS endpoint passed
	 *
	 * @param endpoint Required param.
	 * @returns Promise
	 */
	getPDPData(endpoint: string): Promise<?Object> {
		const host = endpoint.slice(0, endpoint.indexOf('/'));
		const endPath = endpoint.slice(endpoint.indexOf('/'));
		return new Promise((resolve, reject) => {
			const request = https.request({
				hostname: host,
				path: endPath,
				withCredentials: false
			}, (res) => {
				let data = '';
				res.setEncoding('utf8');
				res.on('data', chunk => data += chunk);
				res.on('end', () => {
					try {
						resolve(JSON.parse(data));
					}
					catch (err) {
						resolve(null);
					}
				});
			});

			request.on('error', (e) => {
				reject(e);
			});

			request.end();
		});
	}

	fetchFFCAllVersionData(sapCode: string): Promise<boolean> {
		return new Promise((resolve) => {
			const options = this.getFFCOptions({
				channel: 'ccm',
				headers: {
					withAuth: true,
					withAppId: true
				},
				config: {
					allVersion: true,
					sapCode
				},
				isCCD: false
			}, true);

			const request = https.request({...options}, (res) => {
				let data = '';
				res.on('data', chunk => data += chunk);
				res.on('end', () => {
					AppsUtil.processAndStoreProductFFCData(JSON.parse(data), sapCode);
					AppsUtil.isFFCFallback = false;
					resolve(true);
				});
			});

			request.write(JSON.stringify(this.getMachineState(sapCode)));

			request.on('error', () => {
				this.fetchFFCAllVersionDataFallback(sapCode).then(fallbackStatus => resolve(fallbackStatus), () => resolve(false));
			});
			request.end();
		});
	}

	fetchFFCAllVersionDataFallback(sapCode: string): Promise<boolean> {
		return new Promise((resolve) => {
			const request = https.request({
				hostname: getConfigApps('servers').FFC_fallback.replace('https://',''),
				path: this.getFFCFallbackEndpoint('all', true),
			}, (res) => {
				let data = '';
				res.on('data', chunk => data += chunk);
				res.on('end', () => {
					const ffcData = JSON.parse(data);
					const productSapcodes = AppsUtil.getFFCProductSapcodesSet(ffcData);
					// If ffc doesn't provide any data for a particular sapcode, we want to set it as an empty array instead of letting it be undefined.
					productSapcodes.add(sapCode);
					productSapcodes.forEach((productSapCode) => {
						if (AppsUtil.allVersionsFFCProductList[productSapCode] === undefined) {
							const appData = AppsUtil.filterFallbackFFCData(ffcData, productSapCode);
							AppsUtil.processAndStoreProductFFCData(appData, productSapCode);
						}
					});
					AppsUtil.isFFCFallback = true;
					resolve(true);
				});
			});

			request.on('error', () => {
				resolve(false);
			});

			request.end();
		});
	}

	getPlatformFFCQueryParam() {
		if (DeviceDetectionUtil.isMacOS()) {
			if (AppsUtil.hasFeature('temp_block_arm_platforms')) {
				return 'osx10,osx10-64';
			}
			return 'osx10,osx10-64,macarm64,macuniversal';
		}
		if (DeviceDetectionUtil.isWin()) {
			if (AppsUtil.hasFeature('temp_block_arm_platforms') || DeviceDetectionUtil.isWin7or8()) {
				return 'win32,win64';
			}
			return 'win32,win64,winarm64';
		}
		if (AppsUtil.hasFeature('temp_block_arm_platforms') || DeviceDetectionUtil.isWin7or8()) {
			return 'osx10,osx10-64,win32,win64';
		}
		return 'osx10,osx10-64,macarm64,macuniversal,win32,win64,winarm64';
	}

	getPlatformFFCQueryParamFallback() {
		if (DeviceDetectionUtil.isMacOS()) {
			return '/osx';
		}
		if (DeviceDetectionUtil.isWin()) {
			return '/win';
		}
		return '';
	}
	getPlatformFFCQueryParamFallbackTemp() {
		if (DeviceDetectionUtil.isMacOS()) {
			return '/osx10-64';
		}
		if (DeviceDetectionUtil.isWin()) {
			return DeviceDetectionUtil.is64bitWindows() ? '/win64' : '/win32';
		}
		return '';
	}
}

export default new RestApiUtil();
